import { Button } from "@frontend/components/ui/button"
import { Checkbox } from "@frontend/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@frontend/components/ui/dialog"
import { Input } from "@frontend/components/ui/input"
import { Label } from "@frontend/components/ui/label"
import { useApiClient } from "@frontend/hooks/use-api"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"
import { useCallback, useState } from "react"

import { type SelectedSource, SharedSourceBrowser } from "./shared-source-browser"

interface CaseLinkDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
}

export function CaseLinkDialog({ open, onOpenChange, projectId }: CaseLinkDialogProps) {
  const api = useApiClient()
  const queryClient = useQueryClient()
  const [selectedSources, setSelectedSources] = useState<SelectedSource[]>([])
  const [studyIdentifier, setStudyIdentifier] = useState("")
  const [studyDescription, setStudyDescription] = useState("")
  const [casePerFile, setCasePerFile] = useState(false)
  const [casePrefix, setCasePrefix] = useState("")

  const handleSelectionChange = useCallback((sources: SelectedSource[]) => {
    setSelectedSources(sources)
    // Auto-fill study identifier from common parent directory name
    if (sources.length > 0) {
      setStudyIdentifier((current) => {
        if (current) return current
        const firstPath = sources[0]?.path
        if (!firstPath) return current
        const parts = firstPath.split("/")
        return parts[parts.length - 2] ?? current
      })
    }
  }, [])

  const linkMutation = useMutation({
    mutationFn: async () => {
      return api(`/projects/${projectId}/cases/link-from-source`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sources: selectedSources.map((s) => ({
            path: s.path,
            format: s.format,
            series_identifier: s.name
          })),
          study_identifier: casePerFile ? selectedSources[0]?.name : studyIdentifier,
          study_description: studyDescription || null,
          case_per_file: casePerFile,
          case_prefix: casePerFile ? casePrefix.trim() || undefined : undefined
        })
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "cases"]
      })
      handleClose()
    }
  })

  const handleClose = useCallback(() => {
    setSelectedSources([])
    setStudyIdentifier("")
    setStudyDescription("")
    setCasePerFile(false)
    setCasePrefix("")
    onOpenChange(false)
  }, [onOpenChange])

  const canSubmit =
    selectedSources.length > 0 && (casePerFile || studyIdentifier.trim().length > 0) && !linkMutation.isPending

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose()
      }}
    >
      <DialogContent className="sm:max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Link from Source</DialogTitle>
          <DialogDescription>
            Browse shared source directories and select files to link as a new case. Source files remain in place — no
            copying.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          <SharedSourceBrowser onSelectionChange={handleSelectionChange} selectedSources={selectedSources} />

          {selectedSources.length > 0 && (
            <div className="space-y-3 border-t pt-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{selectedSources.length} file(s) selected</p>
                {selectedSources.length > 1 && (
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <Checkbox checked={casePerFile} onCheckedChange={(v) => setCasePerFile(v === true)} />
                    Create case per file
                  </label>
                )}
              </div>

              {!casePerFile && (
                <div className="space-y-1.5">
                  <Label htmlFor="study-id" className="text-xs">
                    Study Identifier *
                  </Label>
                  <Input
                    id="study-id"
                    value={studyIdentifier}
                    onChange={(e) => setStudyIdentifier(e.target.value)}
                    placeholder="e.g. patient-001-biopsy"
                    className="h-8"
                  />
                </div>
              )}

              {casePerFile && (
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground">
                    Each file will become a separate case using its filename as the study identifier.
                  </p>
                  <Label htmlFor="case-prefix" className="text-xs">
                    Study Identifier Prefix
                  </Label>
                  <Input
                    id="case-prefix"
                    value={casePrefix}
                    onChange={(e) => setCasePrefix(e.target.value)}
                    placeholder="e.g. 001-"
                    className="h-8"
                  />
                  {casePrefix.trim() && selectedSources[0] && (
                    <p className="text-xs text-muted-foreground">
                      Example: {casePrefix.trim()}
                      {selectedSources[0].name.replace(/\.[^/.]+$/, "")}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="study-desc" className="text-xs">
                  Description
                </Label>
                <Input
                  id="study-desc"
                  value={studyDescription}
                  onChange={(e) => setStudyDescription(e.target.value)}
                  placeholder="Optional description"
                  className="h-8"
                />
              </div>
            </div>
          )}

          {linkMutation.isError && (
            <p className="text-xs text-destructive">
              {linkMutation.error instanceof Error ? linkMutation.error.message : "Failed to link case"}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={() => linkMutation.mutate()}>
            {linkMutation.isPending && <Loader2 className="size-3.5 mr-1.5 animate-spin" />}
            {casePerFile ? `Link ${selectedSources.length} Case(s)` : `Link ${selectedSources.length} File(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
